import React, { useState, useEffect } from 'react';
import { auth, googleProvider } from '../firebase';
import { signOut, signInWithPopup, signInWithRedirect, getRedirectResult } from 'firebase/auth';
import useNotifications from '../hooks/useNotifications';

export default function Profile({ user, onLogout, onLogin, navigate }) {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const { permission, requestPermission } = useNotifications();

  const isNative = window.Capacitor?.isNativePlatform?.() ?? false;

  useEffect(() => {
    if (!isNative) return;
    getRedirectResult(auth).then(result => {
      if (result?.user) onLogin(result.user);
    }).catch(() => {});
  }, []);


  const handleGoogleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      if (isNative) {
        await signInWithRedirect(auth, googleProvider);
      } else {
        const result = await signInWithPopup(auth, googleProvider);
        onLogin(result.user);
      }
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user') {
        setError('No se pudo iniciar sesión. Intenta de nuevo.');
      }
    }
    setLoading(false);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (e) {
      console.error('Logout error:', e);
    }
    onLogout();
  };

  return (
    <>
      <h2 className="page-title">👤 Mi Perfil</h2>

      {/* Profile Card */}
      <div className="card" style={{ textAlign: 'center', padding: '32px 24px' }}>
        {user?.photoURL && !user?.isAnonymous ? (
          <img
            src={user.photoURL}
            alt="Profile"
            style={{ width: 84, height: 84, borderRadius: '50%', marginBottom: 16, border: '3px solid var(--gold)' }}
          />
        ) : (
          <div style={{
            width: 84, height: 84, borderRadius: '50%', background: '#f0f2f7',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '2.2rem', margin: '0 auto 16px',
            border: '3px solid var(--border)',
          }}>
            👤
          </div>
        )}
        <h3 style={{ fontSize: '1.2rem', color: 'var(--navy)', marginBottom: 4 }}>
          {user?.isAnonymous ? 'Invitado' : (user?.displayName || 'Miembro')}
        </h3>
        <p style={{ color: 'var(--text-sub)', fontSize: '0.88rem' }}>
          {user?.isAnonymous ? 'Navegando como invitado' : user?.email}
        </p>
        <div style={{
          display: 'inline-block',
          background: user?.isAnonymous ? '#f5f5f5' : '#f0f4ff',
          borderRadius: 20, padding: '4px 14px', marginTop: 12,
          fontSize: '0.78rem', fontWeight: 600,
          color: user?.isAnonymous ? '#888' : 'var(--navy)',
        }}>
          {user?.isAnonymous ? '👤 Invitado' : '✝ Miembro de Bethesda Español'}
        </div>
      </div>

      {/* Guest — sign in or continue as guest */}
      {user?.isAnonymous && (
        <div className="card" style={{ textAlign: 'center' }}>
          <p style={{ fontSize: '0.9rem', color: 'var(--text-sub)', marginBottom: 20, lineHeight: 1.6 }}>
            Inicia sesión para guardar tu historial de donaciones y peticiones de oración.
          </p>

          {/* Google Sign In */}
          <button
            onClick={handleGoogleLogin}
            disabled={loading}
            style={{
              width: '100%', padding: '14px 20px',
              border: '1.5px solid #e5e7eb', borderRadius: 12,
              background: 'white', display: 'flex',
              alignItems: 'center', justifyContent: 'center', gap: 12,
              fontSize: '0.95rem', fontWeight: 700, color: '#1a1a2e',
              cursor: 'pointer', fontFamily: 'Inter, sans-serif',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              marginBottom: 12,
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {loading ? 'Iniciando sesión...' : 'Continuar con Google'}
          </button>

          {error && <p style={{ color: '#ef4444', fontSize: '0.85rem', marginTop: 12 }}>{error}</p>}
        </div>
      )}

      {/* Logged in */}
      {!user?.isAnonymous && (
        <>
          <div className="card">
            <h3 style={{ marginBottom: 16 }}>Mi Cuenta</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div onClick={requestPermission} style={{
                display: 'flex', alignItems: 'center', gap: 14,
                padding: '14px 16px', background: '#f9fafb',
                borderRadius: 12, cursor: 'pointer',
              }}>
                <span style={{ fontSize: '1.4rem' }}>🔔</span>
                <div>
                  <p style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--navy)' }}>Notificaciones</p>
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-sub)' }}>{permission === 'granted' ? '✅ Activadas' : 'Toca para activar'}</p>
                </div>
                <span style={{ marginLeft: 'auto', color: '#ccc', fontSize: '1.2rem' }}>›</span>
              </div>
            </div>
          </div>

          <div className="card">
            <button onClick={handleLogout} style={{
              width: '100%', padding: '13px',
              border: '1.5px solid #fee2e2', borderRadius: 10,
              background: '#fff5f5', color: '#ef4444',
              fontSize: '0.95rem', fontWeight: 700,
              cursor: 'pointer', fontFamily: 'Inter, sans-serif',
            }}>
              Cerrar Sesión
            </button>
          </div>
        </>
      )}
      {/* Privacy Policy */}
      <div style={{ textAlign: 'center', paddingBottom: 8 }}>
        <button
          onClick={() => navigate && navigate('privacy-policy')}
          style={{ background: 'none', border: 'none', color: 'var(--text-sub)', fontSize: '0.78rem', cursor: 'pointer', textDecoration: 'underline' }}
        >
          Política de Privacidad
        </button>
      </div>
    </>
  );
}
